// LayoutBuilder — full customization editor (rows + widgets + widths).
// Used by:
//   - User: /simulator personalization button → edit own layout
//   - Admin: /admin/layout-presets → edit group default_rows
//
// Backed by /api/simulator-layout endpoints (v2).
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, Save, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPAN_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const VIEWS = [
  { id: "outsourcing_contract", label: "Outsourcing — Contrato" },
  { id: "outsourcing_green_receipts", label: "Outsourcing — Recibos Verdes" },
  { id: "outsourcing_b2b", label: "Outsourcing — B2B" },
  { id: "perm", label: "Perm" },
];

/**
 * Props:
 *   getAuthHeaders: fn
 *   mode: "user" | "admin"
 *   adminGroupId?: string (required when mode==="admin")
 *   currentView?: string (default view being edited when mode==="user")
 *   onSaved?: fn
 */
export default function LayoutBuilder({ getAuthHeaders, mode = "user", adminGroupId = null, currentView, onSaved, onRowsChange, initialRows, hideSaveButton = false, isAdminUser = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(initialRows || []);
  const [catalog, setCatalog] = useState([]);
  const [customWidgets, setCustomWidgets] = useState([]);
  const [availableIds, setAvailableIds] = useState([]);
  const [mandatoryIds, setMandatoryIds] = useState([]);
  const [allowedViews, setAllowedViews] = useState(VIEWS.map((v) => v.id));
  const [showPicker, setShowPicker] = useState(null); // {rowIdx, position}
  const [editingView, setEditingView] = useState(currentView || "outsourcing_contract");

  useEffect(() => {
    if (onRowsChange) onRowsChange(rows);
  }, [rows]);

  useEffect(() => {
    (async () => {
      try {
        const catRes = await axios.get(`${API}/widget-catalog`, { headers: getAuthHeaders() });
        setCatalog(catRes.data.catalog || []);
        if (mode === "user") {
          const layoutRes = await axios.get(`${API}/simulator-layout`, { headers: getAuthHeaders(), params: { view: editingView } });
          setRows(layoutRes.data.rows || []);
          setAvailableIds(layoutRes.data.available_widget_ids || []);
          setMandatoryIds(layoutRes.data.mandatory_widget_ids || []);
          setCustomWidgets(layoutRes.data.custom_widgets || []);
          const av = layoutRes.data.allowed_views || VIEWS.map((v) => v.id);
          setAllowedViews(av);
          // If current editing view isn't allowed, switch to first allowed
          if (av.length > 0 && !av.includes(editingView)) {
            setEditingView(av[0]);
          }
        } else if (mode === "admin" && adminGroupId) {
          const cfgRes = await axios.get(`${API}/group-layout-config/admin/${adminGroupId}`, { headers: getAuthHeaders() });
          setRows(cfgRes.data.default_rows || []);
          setAvailableIds(cfgRes.data.available_widget_ids || catRes.data.catalog.map((w) => w.id));
          setMandatoryIds(cfgRes.data.mandatory_widget_ids || []);
          setCustomWidgets(cfgRes.data.custom_widgets || []);
        }
      } catch (err) {
        toast.error("Erro ao carregar layout: " + (err?.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, adminGroupId, editingView, getAuthHeaders]);

  const catalogById = useMemo(() => {
    const map = {};
    [...catalog, ...customWidgets].forEach((w) => { map[w.id] = w; });
    return map;
  }, [catalog, customWidgets]);

  const allWidgetIds = useMemo(() => [...catalog.map((w) => w.id), ...customWidgets.map((w) => w.id)], [catalog, customWidgets]);
  const usableIds = mode === "admin" ? allWidgetIds : availableIds;

  // Widgets currently placed
  const placedIds = useMemo(() => new Set(rows.flatMap((r) => r.widgets.map((w) => w.id))), [rows]);
  const availableToAdd = usableIds.filter((id) => !placedIds.has(id));

  // ================== ROW ACTIONS ==================
  const addRow = () => setRows([...rows, { widgets: [] }]);
  const removeRow = (idx) => {
    if (!window.confirm("Remover esta linha e todos os widgets nela?")) return;
    setRows(rows.filter((_, i) => i !== idx));
  };
  const moveRow = (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= rows.length) return;
    const next = [...rows];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setRows(next);
  };

  // ================== WIDGET ACTIONS ==================
  const addWidgetToRow = (rowIdx, widgetId) => {
    const w = catalogById[widgetId];
    const span = w?.default_span || 4;
    const next = [...rows];
    next[rowIdx] = { ...next[rowIdx], widgets: [...next[rowIdx].widgets, { id: widgetId, w: span }] };
    setRows(next);
    setShowPicker(null);
  };
  const removeWidget = (rowIdx, wIdx) => {
    const wid = rows[rowIdx].widgets[wIdx].id;
    if (mode === "user" && mandatoryIds.includes(wid)) {
      toast.error("Este widget é obrigatório e não pode ser removido (definido pelo admin)");
      return;
    }
    const next = [...rows];
    next[rowIdx] = { ...next[rowIdx], widgets: next[rowIdx].widgets.filter((_, i) => i !== wIdx) };
    setRows(next);
  };
  const changeWidgetSpan = (rowIdx, wIdx, span) => {
    const next = [...rows];
    next[rowIdx] = {
      ...next[rowIdx],
      widgets: next[rowIdx].widgets.map((w, i) => (i === wIdx ? { ...w, w: Number(span) } : w)),
    };
    setRows(next);
  };
  const moveWidget = (rowIdx, wIdx, delta) => {
    const newIdx = wIdx + delta;
    const row = rows[rowIdx];
    if (newIdx < 0 || newIdx >= row.widgets.length) return;
    const next = [...rows];
    const arr = [...row.widgets];
    [arr[wIdx], arr[newIdx]] = [arr[newIdx], arr[wIdx]];
    next[rowIdx] = { ...row, widgets: arr };
    setRows(next);
  };

  // ================== PERSIST ==================
  const save = async () => {
    setSaving(true);
    try {
      if (mode === "user") {
        await axios.put(`${API}/simulator-layout/user`, { custom_rows: rows, view: editingView }, { headers: getAuthHeaders() });
        toast.success(`Layout guardado para "${VIEWS.find((v) => v.id === editingView)?.label || editingView}"!`);
      } else if (mode === "admin" && adminGroupId) {
        await axios.put(`${API}/group-layout-config/admin`, {
          group_id: adminGroupId,
          available_widget_ids: availableIds,
          mandatory_widget_ids: mandatoryIds,
          default_rows: rows,
          custom_widgets: customWidgets,
          allow_user_customization: true,
        }, { headers: getAuthHeaders() });
        toast.success("Configuração do grupo guardada!");
      }
      onSaved?.();
    } catch (err) {
      toast.error("Erro ao guardar: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm(`Repor o layout para "${VIEWS.find((v) => v.id === editingView)?.label}" para o padrão do grupo?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/simulator-layout/user`, { headers: getAuthHeaders(), params: { view: editingView } });
      const layoutRes = await axios.get(`${API}/simulator-layout`, { headers: getAuthHeaders(), params: { view: editingView } });
      setRows(layoutRes.data.rows || []);
      toast.success("Layout reposto para o padrão");
      onSaved?.();
    } catch (err) {
      toast.error("Erro ao repor: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">A carregar layout…</div>;

  return (
    <div className="space-y-4" data-testid="layout-builder">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-slate-600">
            {mode === "user" && isAdminUser
              ? "⚠️ Personalizar como admin: este layout será aplicado a TODOS os admins do grupo. Use o editor de grupo em Admin → Layouts para configurar outros grupos."
              : mode === "user"
              ? "Personalize o seu simulador. Reordene, redimensione, mostre/esconda widgets."
              : "Configuração do grupo. Estes widgets e este layout serão o padrão para todos os utilizadores deste grupo."}
          </div>
          {mode === "user" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Vista:</span>
              <Select value={editingView} onValueChange={setEditingView}>
                <SelectTrigger className="h-8 w-64 text-xs" data-testid="view-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VIEWS.filter((v) => allowedViews.includes(v.id)).map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {mode === "user" && (
            <Button variant="outline" size="sm" onClick={resetToDefault} disabled={saving} data-testid="reset-layout-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Repor
            </Button>
          )}
          {!hideSaveButton && (
          <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/90" data-testid="save-layout-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "A guardar…" : "Guardar Layout"}
          </Button>
          )}
        </div>
      </div>

      {/* ROWS */}
      {rows.map((row, rowIdx) => {
        const usedCols = row.widgets.reduce((sum, w) => sum + (w.w || 0), 0);
        return (
          <Card key={rowIdx} className={`${usedCols > 12 ? "border-red-400" : "border-slate-200"}`} data-testid={`row-${rowIdx}`}>
            <div className="px-3 py-2 flex justify-between items-center border-b bg-slate-50">
              <span className="text-xs font-medium text-slate-500">
                LINHA {rowIdx + 1} — {usedCols}/12 colunas {usedCols > 12 && <span className="text-red-600 ml-1">(excede!)</span>}
              </span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => moveRow(rowIdx, -1)} disabled={rowIdx === 0} className="h-7 w-7 p-0" data-testid={`row-${rowIdx}-up`}>
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => moveRow(rowIdx, 1)} disabled={rowIdx === rows.length - 1} className="h-7 w-7 p-0" data-testid={`row-${rowIdx}-down`}>
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeRow(rowIdx)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`row-${rowIdx}-remove`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-3 grid grid-cols-12 gap-2">
              {row.widgets.map((w, wIdx) => {
                const meta = catalogById[w.id];
                const isMandatory = mandatoryIds.includes(w.id);
                return (
                  <div
                    key={wIdx}
                    className={`col-span-${w.w} border-2 border-dashed rounded-md p-2 bg-white ${isMandatory ? "border-amber-300" : "border-slate-200"}`}
                    style={{ gridColumn: `span ${w.w}` }}
                    data-testid={`widget-${rowIdx}-${wIdx}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-brand-700 truncate">{meta?.name || w.id}</div>
                        <div className="text-[10px] text-slate-500 truncate">{meta?.description || "Widget custom"}</div>
                        {isMandatory && <Badge variant="outline" className="mt-1 text-[10px] border-amber-300 text-amber-700">Obrigatório</Badge>}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-1 flex-wrap">
                      <Select value={String(w.w)} onValueChange={(v) => changeWidgetSpan(rowIdx, wIdx, v)}>
                        <SelectTrigger className="h-7 w-16 text-xs px-2" data-testid={`widget-${rowIdx}-${wIdx}-span`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SPAN_OPTIONS.map((s) => <SelectItem key={s} value={String(s)}>{s}/12</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => moveWidget(rowIdx, wIdx, -1)} disabled={wIdx === 0} className="h-7 w-7 p-0">
                        <ChevronLeft className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => moveWidget(rowIdx, wIdx, 1)} disabled={wIdx === row.widgets.length - 1} className="h-7 w-7 p-0">
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removeWidget(rowIdx, wIdx)} disabled={isMandatory} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {/* Add widget button */}
              <div className="col-span-12" style={{ gridColumn: "span 12" }}>
                {showPicker?.rowIdx === rowIdx ? (
                  <Card className="border-brand-200">
                    <CardContent className="p-3">
                      <div className="text-xs font-medium text-slate-500 mb-2">Escolha um widget para adicionar:</div>
                      <div className="grid grid-cols-3 gap-2">
                        {availableToAdd.length === 0 && (
                          <div className="col-span-3 text-xs text-slate-500 text-center py-2">Todos os widgets disponíveis já estão a ser usados</div>
                        )}
                        {availableToAdd.map((id) => {
                          const meta = catalogById[id];
                          return (
                            <button key={id} onClick={() => addWidgetToRow(rowIdx, id)} className="text-left p-2 border rounded hover:border-brand-400 hover:bg-brand-50 transition" data-testid={`add-widget-${id}`}>
                              <div className="text-xs font-medium text-brand-700">{meta?.name || id}</div>
                              <div className="text-[10px] text-slate-500 line-clamp-1">{meta?.description}</div>
                            </button>
                          );
                        })}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setShowPicker(null)} className="mt-2">Cancelar</Button>
                    </CardContent>
                  </Card>
                ) : (
                  <button onClick={() => setShowPicker({ rowIdx })} className="w-full border-2 border-dashed border-slate-200 rounded-md p-2 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50 transition" data-testid={`add-widget-btn-${rowIdx}`}>
                    <Plus className="w-3 h-3 inline mr-1" /> Adicionar widget nesta linha
                  </button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
      <Button variant="outline" onClick={addRow} className="w-full border-dashed" data-testid="add-row-btn">
        <Plus className="w-4 h-4 mr-2" /> Adicionar nova linha
      </Button>
    </div>
  );
}
